import { useState, useEffect, useCallback } from "react";

import { Spinner } from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  ApplicantParentAccordion,
  SecondParentAccordion,
  ChildOverviewAccordion,
  GuardianAccordion,
  SchoolInfoAccordion,
  OtherQuestionsAccordion,
  SchoolDataType,
  GuardianData,
} from "./components";
import { useAppDispatch } from "../../../app/store";
import {
  ToolbarTop,
  ToolbarEdit,
  ContentArea,
  FormCard,
} from "../../../components";
import { RequestExpandablePanel } from "../../../components/FormControls/RequestExpandablePanel";
import { SaveResult, ValidationFailure } from "../../../models";
import { RegistrationFormDataChangeDto } from "../../../models/Request/RegistrationFormDataChange";
import {
  useGetRegistrationFormDataChangeQuery,
  useSaveRegistrationFormDataChangeMutation,
} from "../../../queries";
import { setIsEdited } from "../../../slices/applicationSlice";
import { DEFAULT_PERSON_DETAILS } from "../constants";

export const EditEnrollmentForm = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { data: initialRegistrationForm, isLoading } =
    useGetRegistrationFormDataChangeQuery(Number(id));
  const [saveRegistrationFormDataChange] =
    useSaveRegistrationFormDataChangeMutation();
  const [formData, setFormData] =
    useState<RegistrationFormDataChangeDto | null>(null);
  const [errors, setErrors] = useState<ValidationFailure[]>([]);

  useEffect(() => {
    if (!initialRegistrationForm) return;

    setFormData(initialRegistrationForm);
  }, [initialRegistrationForm]);

  const handleFormDataChange = useCallback(
    (updatedData: Partial<RegistrationFormDataChangeDto>) => {
      setFormData((prev) => (prev ? { ...prev, ...updatedData } : null));
      dispatch(setIsEdited());
    },
    [dispatch]
  );

  const handleSchoolInfoUpdate = useCallback(
    (schoolData: SchoolDataType) => {
      if (!formData) return;

      handleFormDataChange(schoolData);
    },
    [formData, handleFormDataChange]
  );

  const handleGuardianUpdate = (updatedFields: Partial<GuardianData>) => {
    if (!formData) return;

    handleFormDataChange({
      accommodationFirstName:
        updatedFields.firstName ?? formData.accommodationFirstName ?? "",
      accommodationLastName:
        updatedFields.lastName ?? formData.accommodationLastName ?? "",
      accommodationPhone:
        updatedFields.phoneNumber ?? formData.accommodationPhone ?? "",
      accommodationEmail:
        updatedFields.email ?? formData.accommodationEmail ?? "",
      accommodationRemark:
        updatedFields.note ?? formData.accommodationRemark ?? "",
    });
  };

  const handleSave = async (): Promise<
    SaveResult<RegistrationFormDataChangeDto>
  > => {
    if (!formData) {
      return {
        isValid: false,
        result: formData ?? undefined,
        validationFailures: [],
      };
    }

    return await saveRegistrationFormDataChange({
      requestId: Number(id),
      data: formData,
    }).unwrap();
  };

  if (isLoading) {
    return <Spinner show />;
  }

  return (
    <ContentArea>
      <FormCard>
        <ToolbarTop
          pageTitle={t("enrollmentForm.editFormTitle")}
          showBack="show"
          errors={errors}
        />

        <RequestExpandablePanel
          panelTitle={t("enrollmentForm.sections.applicantParent")}
        >
          <ApplicantParentAccordion
            parentDetails={
              formData?.applicantParentDetails ?? DEFAULT_PERSON_DETAILS
            }
            applicantParent={formData?.applicantParent}
            onUpdate={(updatedDetails) =>
              handleFormDataChange({ applicantParentDetails: updatedDetails })
            }
          />
        </RequestExpandablePanel>
        {formData?.otherParentDetails && (
          <RequestExpandablePanel
            panelTitle={t("enrollmentForm.sections.secondParent")}
          >
            <SecondParentAccordion
              parentDetails={
                formData?.otherParentDetails ?? DEFAULT_PERSON_DETAILS
              }
              parentIdentification={formData?.otherParent}
              onUpdate={(updatedDetails) =>
                handleFormDataChange({ otherParentDetails: updatedDetails })
              }
            />
          </RequestExpandablePanel>
        )}
        <RequestExpandablePanel
          panelTitle={t("enrollmentForm.sections.childOverview")}
        >
          <ChildOverviewAccordion
            childIdentification={formData?.child}
            childDetails={formData?.childDetails ?? DEFAULT_PERSON_DETAILS}
            onUpdate={(updatedDetails) =>
              handleFormDataChange({ childDetails: updatedDetails })
            }
          />
        </RequestExpandablePanel>
        <RequestExpandablePanel
          panelTitle={t("enrollmentForm.sections.guardian")}
        >
          <GuardianAccordion
            onUpdate={handleGuardianUpdate}
            guardianData={{
              firstName: formData?.accommodationFirstName ?? "",
              lastName: formData?.accommodationLastName ?? "",
              phoneNumber: formData?.accommodationPhone ?? "",
              email: formData?.accommodationEmail ?? "",
              note: formData?.accommodationRemark ?? "",
            }}
          />
        </RequestExpandablePanel>
        {!!formData?.enrollmentInstitution && (
          <RequestExpandablePanel
            panelTitle={t("enrollmentForm.sections.schoolInfo")}
          >
            <SchoolInfoAccordion
              onUpdate={handleSchoolInfoUpdate}
              schoolData={{
                enrollmentInstitution: formData.enrollmentInstitution,
                enrollmentInstitutionTown: formData.enrollmentInstitutionTown,
                enrollmentInstitutionMunicipality:
                  formData.enrollmentInstitutionMunicipality,
                enrollmentInstitutionCounty:
                  formData.enrollmentInstitutionCounty,
                educationalProgram: formData.educationalProgram,
                instituionSchoolYearMandatoryForeignLanguages:
                  formData.instituionSchoolYearMandatoryForeignLanguages,
                institutionSchoolYearElectiveSubjects:
                  formData.institutionSchoolYearElectiveSubjects,
              }}
            />
          </RequestExpandablePanel>
        )}
        <RequestExpandablePanel panelTitle={t("enrollmentForm.sections.other")}>
          <OtherQuestionsAccordion
            questionAnswers={formData?.questionAnswers ?? []}
            onUpdate={(updatedAnswers) =>
              handleFormDataChange({ questionAnswers: updatedAnswers })
            }
            specialData={formData?.specialData ?? ""}
            onSpecialDataUpdate={(updatedSpecialData) =>
              handleFormDataChange({ specialData: updatedSpecialData })
            }
          />
        </RequestExpandablePanel>
        <ToolbarEdit
          showCancel="show"
          showSave="show"
          handleSave={handleSave}
          onSaveSuccess={() => history.goBack()}
          setSaveErrors={setErrors}
        />
      </FormCard>
    </ContentArea>
  );
};
